import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { formatBlogDate, BLOG_FALLBACK_IMAGE, type BlogPost } from "@/lib/blog";

export const BlogCard = ({ post }: { post: BlogPost }) => (
  <Link
    to={`/blog/${post.slug}`}
    className="group flex h-full flex-col bg-card border border-border overflow-hidden transition-all duration-300 hover:border-primary/60 hover:-translate-y-1 hover:shadow-[var(--shadow-luxury)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
  >
    <div className="aspect-video overflow-hidden bg-secondary">
      <img
        src={post.image}
        alt={post.title}
        loading="lazy"
        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
        onError={(e) => {
          const img = e.currentTarget;
          if (img.getAttribute("src") !== BLOG_FALLBACK_IMAGE) img.src = BLOG_FALLBACK_IMAGE;
        }}
      />
    </div>
    <div className="flex flex-1 flex-col p-6 md:p-7">
      <p className="eyebrow-sm text-primary mb-3">{post.categories[0]}</p>
      <h3 className="font-serif text-xl md:text-2xl font-light leading-snug text-foreground line-clamp-2 group-hover:text-primary transition-colors">
        {post.title}
      </h3>
      <p className="mt-2 text-xs tracking-widest uppercase text-foreground/70">{formatBlogDate(post.date)}</p>
      <p className="mt-4 text-base text-foreground/80 font-light leading-relaxed line-clamp-3">{post.excerpt}</p>
      <span className="mt-auto pt-6 inline-flex items-center gap-2 text-sm font-bold tracking-[0.2em] uppercase text-primary">
        Read More <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
      </span>
    </div>
  </Link>
);

export const BlogCardSkeleton = () => (
  <div className="bg-card border border-border overflow-hidden animate-pulse">
    <div className="aspect-video bg-secondary" />
    <div className="p-7 space-y-4">
      <div className="h-3 w-24 bg-secondary" />
      <div className="h-5 w-full bg-secondary" />
      <div className="h-5 w-3/4 bg-secondary" />
      <div className="h-3 w-full bg-secondary" />
      <div className="h-3 w-5/6 bg-secondary" />
    </div>
  </div>
);

export const BlogCardGrid = ({ posts }: { posts: BlogPost[] }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
    {posts.map(post => <BlogCard key={post.slug} post={post} />)}
  </div>
);
